import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useEffect, useRef, useState } from "react";
import { streamAI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { X, Send, ChevronRight, Lock } from "lucide-react";

const SESSION_KEY = "rigcalc-ai-session";

export default function AIAssistant({ onClose, context }) {
  const { isAuthed, hasPro, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: "sys",
      role: "system",
      content:
        "RIGCALC AI // Claude Sonnet 5\nAsk about formulas, kill sheets, tubing movement, or safety checks.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const sessionId =
    localStorage.getItem(SESSION_KEY) ||
    (() => {
      const id = "sess-" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(SESSION_KEY, id);
      return id;
    })();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = () => {
    if (!input.trim() || streaming) return;
    if (!isAuthed) {
      setShowAuth(true);
      return;
    }
    if (!hasPro) {
      navigate("/pricing");
      return;
    }
    const userMsg = input.trim();
    setInput("");
    const t = Date.now();
    setMessages((m) => [...m, { id: `u${t}`, role: "user", content: userMsg }, { id: `a${t}`, role: "assistant", content: "" }]);
    setStreaming(true);
    streamAI(
      sessionId,
      userMsg,
      context,
      (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: (copy[copy.length - 1].content || "") + chunk };
          return copy;
        });
      },
      () => setStreaming(false),
      (err) => {
        if (err.message === "PAYWALL") {
          setMessages((m) => [...m.slice(0, -1)]);
          navigate("/pricing");
        } else {
          setMessages((m) => [...m, { id: `e${Date.now()}`, role: "error", content: `ERROR: ${err.message}` }]);
        }
        setStreaming(false);
      }
    );
  };

  const disabled = !isAuthed || !hasPro;

  return (
    <aside data-testid="ai-panel" className="w-full lg:w-[400px] h-full shrink-0 border-l border-[#334155] bg-[#11151c] flex flex-col terminal-scan">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#334155]">
        <div className="flex items-center gap-2 text-xs mono tracking-widest text-[#f59e0b]">
          <span className="w-1.5 h-1.5 bg-[#f59e0b] animate-pulse" />
          <span className="hidden sm:inline">RIGCALC://AI-CLAUDE-SONNET-5</span>
          <span className="sm:hidden">AI · CLAUDE-5</span>
        </div>
        <button data-testid="close-ai-btn" onClick={onClose} className="text-[#94a3b8] hover:text-[#f8fafc]">
          <X className="w-4 h-4" />
        </button>
      </div>

      {disabled && (
        <div className="border-b border-[#334155] bg-[#f59e0b]/10 px-4 py-3">
          <div className="flex items-start gap-2 text-xs mono text-[#fbbf24]">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              {isAuthed
                ? "Pro plan required for AI assistant."
                : "Sign in and upgrade to Pro to use the AI assistant."}
              <button
                onClick={() => (isAuthed ? navigate("/pricing") : setShowAuth(true))}
                data-testid="ai-upgrade-btn"
                className="ml-2 underline hover:text-[#f8fafc]"
              >
                {isAuthed ? "See plans →" : "Sign in →"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 mono text-[13px] leading-relaxed">
        {messages.map((m, i) => (
          <div key={m.id} className="whitespace-pre-wrap">
            {m.role === "system" && (
              <div className="text-[#64748b] border-l-2 border-[#334155] pl-3">{m.content}</div>
            )}
            {m.role === "user" && (
              <div className="text-[#f8fafc]">
                <span className="text-[#f59e0b]">&gt;</span> {m.content}
              </div>
            )}
            {m.role === "assistant" && (
              <div data-testid="ai-answer" className="ai-md border-l-2 border-[#f59e0b] pl-3 text-[#f8fafc] whitespace-normal">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                {streaming && i === messages.length - 1 && <span className="blink-cursor" />}
              </div>
            )}
            {m.role === "error" && (
              <div className="text-[#ef4444] border-l-2 border-[#ef4444] pl-3">{m.content}</div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-[#334155] p-3">
        <div className="flex items-center gap-2 border border-[#334155] focus-within:border-[#f59e0b] bg-[#0b0e13] px-2">
          <ChevronRight className="w-4 h-4 text-[#f59e0b]" />
          <input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={disabled ? "sign in / upgrade to chat..." : "ask about a formula or scenario..."}
            className="flex-1 bg-transparent text-sm py-2 mono focus:outline-none placeholder:text-[#475569]"
            disabled={streaming}
          />
          <button
            data-testid="ai-send-btn"
            onClick={send}
            disabled={streaming || !input.trim()}
            className="p-1.5 text-[#f59e0b] hover:text-[#fbbf24] disabled:opacity-30"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
